/**
 * This file contains all possible ids 'owned' by us.
 * It's important to include all ids here so that we can
 * help protect against IDOR.
 *
 * Ideally it is kept in alphabetical order
 */
import { z } from "zod";

import { IntrinsicGroupId } from "./enums.js";
import {
  PaymentMethodId,
  StripeCustomerId,
  StripePriceId,
  StripeProductId,
  StripeSubscriptionId,
  StripeSubscriptionItemId,
} from "./stripeIdTypeBrands.js";
import { KernelImage } from "./typeBrands.js";

export const AppSessionAOLogId = z.string().uuid().brand("AppSessionAOLogId");
export type AppSessionAOLogId = z.infer<typeof AppSessionAOLogId>;

export const AppSessionCellId = z.string().uuid().brand("AppSessionCellId");
export type AppSessionCellId = z.infer<typeof AppSessionCellId>;

export const AppSessionEventLogId = z
  .string()
  .uuid()
  .brand("AppSessionEventLogId");
export type AppSessionEventLogId = z.infer<typeof AppSessionEventLogId>;

export const AppSessionGrantId = z.string().uuid().brand("AppSessionGrantId");
export type AppSessionGrantId = z.infer<typeof AppSessionGrantId>;

export const AppSessionId = z.string().uuid().brand("AppSessionId");
export type AppSessionId = z.infer<typeof AppSessionId>;

export const AppSessionDeletionEventId = z
  .string()
  .uuid()
  .brand("AppSessionDeletionEventId");
export type AppSessionDeletionEventId = z.infer<
  typeof AppSessionDeletionEventId
>;

export const SavedViewId = z.string().uuid().brand("SavedViewId");
export type SavedViewId = z.infer<typeof SavedViewId>;

export const ApiClientId = z.string().uuid().brand("ApiClientId");
export type ApiClientId = z.infer<typeof ApiClientId>;

export const BigQueryOAuthConnectionId = z
  .string()
  .uuid()
  .brand("BigQueryOAuthConnectionId");
export type BigQueryOAuthConnectionId = z.infer<
  typeof BigQueryOAuthConnectionId
>;

export const BlockCellId = z.string().uuid().brand("BlockCellId");
export type BlockCellId = z.infer<typeof BlockCellId>;

export const CanvasElementId = z.string().uuid().brand("CanvasElementId");
export type CanvasElementId = z.infer<typeof CanvasElementId>;

export const DataAppId = z.string().uuid().brand("DataAppId");
export type DataAppId = z.infer<typeof DataAppId>;

export const DataAppElementId = z.string().uuid().brand("DataAppElementId");
export type DataAppElementId = z.infer<typeof DataAppElementId>;

export const DataAppFileAOLogId = z.string().uuid().brand("DataAppFileAOLogId");
export type DataAppFileAOLogId = z.infer<typeof DataAppFileAOLogId>;

export const DataAppFileId = z.string().uuid().brand("DataAppFileId");
export type DataAppFileId = z.infer<typeof DataAppFileId>;

export const DataAppFilePath = z.string().brand("DataAppFilePath");
export type DataAppFilePath = z.infer<typeof DataAppFilePath>;

export const CanvasLayoutId = z.string().uuid().brand("CanvasLayoutId");
export type CanvasLayoutId = z.infer<typeof CanvasLayoutId>;

export const CategoryId = z.string().uuid().brand("CategoryId");
export type CategoryId = z.infer<typeof CategoryId>;

export const CellGroupCellId = z.string().uuid().brand("CellGroupCellId");
export type CellGroupCellId = z.infer<typeof CellGroupCellId>;

/**
 * Not uuid-validated, unlike most ids here: kernel cache-eviction payloads carry
 * a composite `${cellId}:$${queryName}` (filterCellSource.ts, sqlCellSource.ts).
 * Agent tool inputs use the strict `zAgentInputCellId` instead.
 */
export const CellId = z.string().brand("CellId");
export type CellId = z.infer<typeof CellId>;

export const ChartCellId = z.string().uuid().brand("ChartCellId");
export type ChartCellId = z.infer<typeof ChartCellId>;

export const ChartCellSessionStateId = z
  .string()
  .uuid()
  .brand("ChartCellSessionStateId");
export type ChartCellSessionStateId = z.infer<typeof ChartCellSessionStateId>;

export const CompactedAppSessionEventLogId = z
  .string()
  .uuid()
  .brand("CompactedAppSessionEventLogId");
export type CompactedAppSessionEventLogId = z.infer<
  typeof CompactedAppSessionEventLogId
>;

export const ExploreCellId = z.string().uuid().brand("ExploreCellId");
export type ExploreCellId = z.infer<typeof ExploreCellId>;

export const CodeCellId = z.string().uuid().brand("CodeCellId");
export type CodeCellId = z.infer<typeof CodeCellId>;

export const CollectionGrantId = z.string().uuid().brand("CollectionGrantId");
export type CollectionGrantId = z.infer<typeof CollectionGrantId>;

export const CollectionHexLinkId = z
  .string()
  .uuid()
  .brand("CollectionHexLinkId");
export type CollectionHexLinkId = z.infer<typeof CollectionHexLinkId>;

export const CollectionId = z.string().uuid().brand("CollectionId");
export type CollectionId = z.infer<typeof CollectionId>;

export const ColumnPropertiesId = z.string().uuid().brand("ColumnPropertiesId");
export type ColumnPropertiesId = z.infer<typeof ColumnPropertiesId>;

export const ColorPaletteId = z.string().uuid().brand("ColorPaletteId");
export type ColorPaletteId = z.infer<typeof ColorPaletteId>;

export const CommentId = z.string().uuid().brand("CommentId");
export type CommentId = z.infer<typeof CommentId>;

export const CommentReactionId = z.string().uuid().brand("CommentReactionId");
export type CommentReactionId = z.infer<typeof CommentReactionId>;

export const ComponentImportCellId = z
  .string()
  .uuid()
  .brand("ComponentImportCellId");
export type ComponentImportCellId = z.infer<typeof ComponentImportCellId>;

export const ComputeActionId = z.string().uuid().brand("ComputeActionId");
export type ComputeActionId = z.infer<typeof ComputeActionId>;

export const ComputeActionBatchId = z
  .string()
  .uuid()
  .brand("ComputeActionBatchId");
/**
 * A non-entity id used to batch compute actions that were queued at the same time together
 */
export type ComputeActionBatchId = z.infer<typeof ComputeActionBatchId>;

export const DataConnectionHexVersionLinkId = z
  .string()
  .uuid()
  .brand("DataConnectionHexVersionLinkId");
export type DataConnectionHexVersionLinkId = z.infer<
  typeof DataConnectionHexVersionLinkId
>;

export const DataConnectionId = z.string().uuid().brand("DataConnectionId");
export type DataConnectionId = z.infer<typeof DataConnectionId>;

export const DataConnectionSchemaRefreshId = z
  .string()
  .uuid()
  .brand("DataConnectionSchemaRefreshId");
export type DataConnectionSchemaRefreshId = z.infer<
  typeof DataConnectionSchemaRefreshId
>;

export const DataConnectionSchemaRefreshHistoryId = z
  .string()
  .uuid()
  .brand("DataConnectionSchemaRefreshHistoryId");
export type DataConnectionSchemaRefreshHistoryId = z.infer<
  typeof DataConnectionSchemaRefreshHistoryId
>;

export const DataConnectionSchemaRefreshScheduleId = z
  .string()
  .uuid()
  .brand("DataConnectionSchemaRefreshScheduleId");
export type DataConnectionSchemaRefreshScheduleId = z.infer<
  typeof DataConnectionSchemaRefreshScheduleId
>;

export const DataConnectionOAuthSettingsId = z
  .string()
  .uuid()
  .brand("DataConnectionOAuthSettingsId");
export type DataConnectionOAuthSettingsId = z.infer<
  typeof DataConnectionOAuthSettingsId
>;

export const DataSourceGrantId = z.string().uuid().brand("DataSourceGrantId");
export type DataSourceGrantId = z.infer<typeof DataSourceGrantId>;

export const DataSourceGrantStableId = z
  .string()
  .brand("DataSourceGrantStableId");
export type DataSourceGrantStableId = z.infer<typeof DataSourceGrantStableId>;

export const SchemaRefreshEntityDiffStableId = z
  .string()
  .brand("SchemaRefreshEntityDiffStableId");
export type SchemaRefreshEntityDiffStableId = z.infer<
  typeof SchemaRefreshEntityDiffStableId
>;

export const DataSourceColumnId = z.string().uuid().brand("DataSourceColumnId");
export type DataSourceColumnId = z.infer<typeof DataSourceColumnId>;

export const DataSourceColumnMetadataId = z
  .string()
  .uuid()
  .brand("DataSourceColumnMetadataId");
export type DataSourceColumnMetadataId = z.infer<
  typeof DataSourceColumnMetadataId
>;

export const DataSourceDatabaseId = z
  .string()
  .uuid()
  .brand("DataSourceDatabaseId");
export type DataSourceDatabaseId = z.infer<typeof DataSourceDatabaseId>;

export const DataSourceDatabaseMetadataId = z
  .string()
  .uuid()
  .brand("DataSourceDatabaseMetadataId");
export type DataSourceDatabaseMetadataId = z.infer<
  typeof DataSourceDatabaseMetadataId
>;

export const DataSourceDbtMetricId = z
  .string()
  .uuid()
  .brand("DataSourceDbtMetricId");
export type DataSourceDbtMetricId = z.infer<typeof DataSourceDbtMetricId>;

export const DataSourceSchemaId = z.string().uuid().brand("DataSourceSchemaId");
export type DataSourceSchemaId = z.infer<typeof DataSourceSchemaId>;

export const DataSourceSchemaMetadataId = z
  .string()
  .uuid()
  .brand("DataSourceSchemaMetadataId");
export type DataSourceSchemaMetadataId = z.infer<
  typeof DataSourceSchemaMetadataId
>;

export const DataSourceTableId = z.string().uuid().brand("DataSourceTableId");
export type DataSourceTableId = z.infer<typeof DataSourceTableId>;

export const DataSourceTableMetadataId = z
  .string()
  .uuid()
  .brand("DataSourceTableMetadataId");
export type DataSourceTableMetadataId = z.infer<
  typeof DataSourceTableMetadataId
>;

export type DataSourceEntityId =
  | DataSourceColumnId
  | DataSourceTableId
  | DataSourceSchemaId
  | DataSourceDatabaseId;

export const DbtMetricCellDataSourceDbtMetricLinkId = z
  .string()
  .uuid()
  .brand("DbtMetricCellDataSourceDbtMetricLinkId");
export type DbtMetricCellDataSourceDbtMetricLinkId = z.infer<
  typeof DbtMetricCellDataSourceDbtMetricLinkId
>;

export const DatabricksOAuthConnectionId = z
  .string()
  .uuid()
  .brand("DatabricksOAuthConnectionId");
export type DatabricksOAuthConnectionId = z.infer<
  typeof DatabricksOAuthConnectionId
>;

export const DataSourceObjectCategoryLinkId = z
  .string()
  .uuid()
  .brand("DataSourceObjectCategoryLinkId");
export type DataSourceObjectCategoryLinkId = z.infer<
  typeof DataSourceObjectCategoryLinkId
>;

export const DbtMetricCellId = z.string().uuid().brand("DbtMetricCellId");
export type DbtMetricCellId = z.infer<typeof DbtMetricCellId>;

export const DeviceCodeId = z.string().uuid().brand("DeviceCodeId");
export type DeviceCodeId = z.infer<typeof DeviceCodeId>;

export const DisplayTableCellId = z.string().uuid().brand("DisplayTableCellId");
export type DisplayTableCellId = z.infer<typeof DisplayTableCellId>;

export const DisplayTableColumnId = z.string().brand("DisplayTableColumnId");
export type DisplayTableColumnId = z.infer<typeof DisplayTableColumnId>;

export const DisplayTableConfigId = z
  .string()
  .uuid()
  .brand("DisplayTableConfigId");
export type DisplayTableConfigId = z.infer<typeof DisplayTableConfigId>;

export const DisplayTableSessionStateId = z
  .string()
  .uuid()
  .brand("DisplayTableSessionStateId");
export type DisplayTableSessionStateId = z.infer<
  typeof DisplayTableSessionStateId
>;

export const DomainDenialId = z.string().uuid().brand("DomainDenialId");
export type DomainDenialId = z.infer<typeof DomainDenialId>;

export const EmailDenialId = z.string().uuid().brand("EmailDenialId");
export type EmailDenialId = z.infer<typeof EmailDenialId>;

export const EmailVerificationLinkId = z
  .string()
  .uuid()
  .brand("EmailVerificationLinkId");
export type EmailVerificationLinkId = z.infer<typeof EmailVerificationLinkId>;

export const EvalBaseId = z.string().uuid().brand("EvalBaseId");
export type EvalBaseId = z.infer<typeof EvalBaseId>;

export const CustomerEvalGradeResultId = z
  .string()
  .uuid()
  .brand("CustomerEvalGradeResultId");
export type CustomerEvalGradeResultId = z.infer<
  typeof CustomerEvalGradeResultId
>;

export const CustomerEvalCaseRunId = z
  .string()
  .uuid()
  .brand("CustomerEvalCaseRunId");
export type CustomerEvalCaseRunId = z.infer<typeof CustomerEvalCaseRunId>;

export const CustomerEvalCaseRunAttemptId = z
  .string()
  .uuid()
  .brand("CustomerEvalCaseRunAttemptId");
export type CustomerEvalCaseRunAttemptId = z.infer<
  typeof CustomerEvalCaseRunAttemptId
>;

export const CustomerEvalSuiteId = z
  .string()
  .uuid()
  .brand("CustomerEvalSuiteId");
export type CustomerEvalSuiteId = z.infer<typeof CustomerEvalSuiteId>;

export const CustomerEvalSuiteScheduleId = z
  .string()
  .uuid()
  .brand("CustomerEvalSuiteScheduleId");
export type CustomerEvalSuiteScheduleId = z.infer<
  typeof CustomerEvalSuiteScheduleId
>;

export const CustomerEvalSuiteVersionId = z
  .string()
  .uuid()
  .brand("CustomerEvalSuiteVersionId");
export type CustomerEvalSuiteVersionId = z.infer<
  typeof CustomerEvalSuiteVersionId
>;

export const CustomerEvalCaseId = z.string().uuid().brand("CustomerEvalCaseId");
export type CustomerEvalCaseId = z.infer<typeof CustomerEvalCaseId>;

export const CustomerEvalCaseVersionId = z
  .string()
  .uuid()
  .brand("CustomerEvalCaseVersionId");
export type CustomerEvalCaseVersionId = z.infer<
  typeof CustomerEvalCaseVersionId
>;

export const CustomerEvalSuiteRunId = z
  .string()
  .uuid()
  .brand("CustomerEvalSuiteRunId");
export type CustomerEvalSuiteRunId = z.infer<typeof CustomerEvalSuiteRunId>;

export const EvalResultId = z.string().uuid().brand("EvalResultId");
export type EvalResultId = z.infer<typeof EvalResultId>;

export const EvalRubricId = z.string().uuid().brand("EvalRubricId");
export type EvalRubricId = z.infer<typeof EvalRubricId>;

export const EvalRunId = z.string().uuid().brand("EvalRunId");
export type EvalRunId = z.infer<typeof EvalRunId>;

export const EvalSetId = z.string().uuid().brand("EvalSetId");
export type EvalSetId = z.infer<typeof EvalSetId>;

export const EvalSetRunId = z.string().uuid().brand("EvalSetRunId");
export type EvalSetRunId = z.infer<typeof EvalSetRunId>;

export const EvalSemanticProjectFileId = z
  .string()
  .uuid()
  .brand("EvalSemanticProjectFileId");
export type EvalSemanticProjectFileId = z.infer<
  typeof EvalSemanticProjectFileId
>;

export const EmbeddedApiCallId = z.string().uuid().brand("EmbeddedApiCallId");
export type EmbeddedApiCallId = z.infer<typeof EmbeddedApiCallId>;

export const EmbeddedOAuthCredentialsId = z
  .string()
  .uuid()
  .brand("EmbeddedOAuthCredentialsId");
export type EmbeddedOAuthCredentialsId = z.infer<
  typeof EmbeddedOAuthCredentialsId
>;

export const EnvVarId = z.string().uuid().brand("EnvVarId");
export type EnvVarId = z.infer<typeof EnvVarId>;

export const ESDocId = z.string().brand("ESDocId");
export type ESDocId = z.infer<typeof ESDocId>;

export const ExternalContextConnectionId = z
  .string()
  .uuid()
  .brand("ExternalContextConnectionId");
export type ExternalContextConnectionId = z.infer<
  typeof ExternalContextConnectionId
>;

export const ExploreAgentSettingsId = z
  .string()
  .uuid()
  .brand("ExploreAgentSettingsId");
export type ExploreAgentSettingsId = z.infer<typeof ExploreAgentSettingsId>;

export const ExploreSpecSessionStateId = z
  .string()
  .uuid()
  .brand("ExploreSpecSessionStateId");
export type ExploreSpecSessionStateId = z.infer<
  typeof ExploreSpecSessionStateId
>;

export const ExternalFileIntegrationId = z
  .string()
  .uuid()
  .brand("ExternalFileIntegrationId");
export type ExternalFileIntegrationId = z.infer<
  typeof ExternalFileIntegrationId
>;

export const ExternalFileIntegrationHexVersionLinkId = z
  .string()
  .uuid()
  .brand("ExternalFileIntegrationHexVersionLinkId");
export type ExternalFileIntegrationHexVersionLinkId = z.infer<
  typeof ExternalFileIntegrationHexVersionLinkId
>;

export const GoogleDriveOauthAccountId = z
  .string()
  .uuid()
  .brand("GoogleDriveOauthAccountId");
export type GoogleDriveOauthAccountId = z.infer<
  typeof GoogleDriveOauthAccountId
>;

export const FileHexVersionLinkId = z
  .string()
  .uuid()
  .brand("FileHexVersionLinkId");
export type FileHexVersionLinkId = z.infer<typeof FileHexVersionLinkId>;

export const FileId = z.string().uuid().brand("FileId");
export type FileId = z.infer<typeof FileId>;

export const FolderEntryId = z.string().uuid().brand("FolderEntryId");
export type FolderEntryId = z.infer<typeof FolderEntryId>;

export const FolderEntryGrantId = z.string().uuid().brand("FolderEntryGrantId");
export type FolderEntryGrantId = z.infer<typeof FolderEntryGrantId>;

export const FolderEntryPath = z.string().brand("FolderEntryPath");
export type FolderEntryPath = z.infer<typeof FolderEntryPath>;

export const AppSessionFileId = z.string().uuid().brand("AppSessionFileId");
export type AppSessionFileId = z.infer<typeof AppSessionFileId>;

export type BaseFileId = FileId | AppSessionFileId;

export const FilterCellColumnId = z.string().brand("FilterCellColumn");
export type FilterCellColumnId = z.infer<typeof FilterCellColumnId>;

export const FilterCellId = z.string().uuid().brand("FilterCellId");
export type FilterCellId = z.infer<typeof FilterCellId>;

export const FilterCellSessionStateId = z
  .string()
  .uuid()
  .brand("FilterCellSessionStateId");
export type FilterCellSessionStateId = z.infer<typeof FilterCellSessionStateId>;

export const GridElementId = z.string().uuid().brand("GridElementId");
export type GridElementId = z.infer<typeof GridElementId>;

export const GridColumnId = z.string().uuid().brand("GridColumnId");
export type GridColumnId = z.infer<typeof GridColumnId>;

export const GridLayoutId = z.string().uuid().brand("GridLayoutId");
export type GridLayoutId = z.infer<typeof GridLayoutId>;

export const GridRowId = z.string().uuid().brand("GridRowId");
export type GridRowId = z.infer<typeof GridRowId>;

export const StaticGridLayoutId = z.string().uuid().brand("StaticGridLayoutId");
export type StaticGridLayoutId = z.infer<typeof StaticGridLayoutId>;

export const GroupId = z.string().uuid().brand("GroupId");
export type GroupId = z.infer<typeof GroupId>;

export const GithubServerConfigId = z
  .string()
  .uuid()
  .brand("GithubServerConfigId");
export type GithubServerConfigId = z.infer<typeof GithubServerConfigId>;

export const HexAnalyticsId = z.string().uuid().brand("HexAnalyticsId");
export type HexAnalyticsId = z.infer<typeof HexAnalyticsId>;

export const HexId = z.string().uuid().brand("HexId");
export type HexId = z.infer<typeof HexId>;

export const HexVersionOAuthSettingsId = z
  .string()
  .uuid()
  .brand("HexVersionOAuthSettingsId");
export type HexVersionOAuthSettingsId = z.infer<
  typeof HexVersionOAuthSettingsId
>;

export const HexScheduleId = z.string().uuid().brand("HexScheduleId");
export type HexScheduleId = z.infer<typeof HexScheduleId>;

export const ThreadExtractionEvalResultId = z
  .string()
  .uuid()
  .brand("ThreadExtractionEvalResultId");
export type ThreadExtractionEvalResultId = z.infer<
  typeof ThreadExtractionEvalResultId
>;

export const ThreadExtractionExperimentId = z
  .string()
  .uuid()
  .brand("ThreadExtractionExperimentId");
export type ThreadExtractionExperimentId = z.infer<
  typeof ThreadExtractionExperimentId
>;
export const ScheduleNotificationId = z
  .string()
  .uuid()
  .brand("ScheduleNotificationId");
export type ScheduleNotificationId = z.infer<typeof ScheduleNotificationId>;

export const ScheduleNotificationExportId = z
  .string()
  .uuid()
  .brand("ScheduleNotificationExportId");
export type ScheduleNotificationExportId = z.infer<
  typeof ScheduleNotificationExportId
>;

export const SavedViewHexScheduleLinkId = z
  .string()
  .uuid()
  .brand("SavedViewHexScheduleLinkId");
export type SavedViewHexScheduleLinkId = z.infer<
  typeof SavedViewHexScheduleLinkId
>;

export const SavedViewScheduleNotificationLinkId = z
  .string()
  .uuid()
  .brand("SavedViewScheduleNotificationLinkId");
export type SavedViewScheduleNotificationLinkId = z.infer<
  typeof SavedViewScheduleNotificationLinkId
>;

export const ScheduleNotificationConditionId = z
  .string()
  .uuid()
  .brand("ScheduleNotificationConditionId");
export type ScheduleNotificationConditionId = z.infer<
  typeof ScheduleNotificationConditionId
>;

export const RunNotificationRecipientId = z
  .string()
  .uuid()
  .brand("RunNotificationRecipientId");
export type RunNotificationRecipientId = z.infer<
  typeof RunNotificationRecipientId
>;

export const SlackChannelId = z.string().uuid().brand("SlackChannelId");
export type SlackChannelId = z.infer<typeof SlackChannelId>;

export const SlackTeamAuthorizationId = z
  .string()
  .uuid()
  .brand("SlackTeamAuthorizationId");
export type SlackTeamAuthorizationId = z.infer<typeof SlackTeamAuthorizationId>;

export const SlackUserId = z.string().uuid().brand("SlackUserId");
export type SlackUserId = z.infer<typeof SlackUserId>;

export const SlackAgentChatThreadLinkId = z
  .string()
  .uuid()
  .brand("SlackAgentChatThreadLinkId");
export type SlackAgentChatThreadLinkId = z.infer<
  typeof SlackAgentChatThreadLinkId
>;

export const HexAOLogId = z.string().uuid().brand("HexAOLogId");
export type HexAOLogId = z.infer<typeof HexAOLogId>;

export const HexVersionAOLogId = z.string().uuid().brand("HexVersionAOLogId");
export type HexVersionAOLogId = z.infer<typeof HexVersionAOLogId>;

export const HexCategoryLinkId = z.string().uuid().brand("HexCategoryLinkId");
export type HexCategoryLinkId = z.infer<typeof HexCategoryLinkId>;

export const TaoCategoryLinkId = z.string().uuid().brand("TaoCategoryLinkId");
export type TaoCategoryLinkId = z.infer<typeof TaoCategoryLinkId>;

export const HexVersionId = z.string().uuid().brand("HexVersionId");
export type HexVersionId = z.infer<typeof HexVersionId>;

export const HexVersionDefaultAppSessionId = z
  .string()
  .uuid()
  .brand("HexVersionDefaultAppSessionId");
export type HexVersionDefaultAppSessionId = z.infer<
  typeof HexVersionDefaultAppSessionId
>;

export const HexVersionSecretId = z.string().uuid().brand("HexVersionSecretId");
export type HexVersionSecretId = z.infer<typeof HexVersionSecretId>;

export const DockerImageRepositoryId = z
  .string()
  .uuid()
  .brand("DockerImageRepositoryId");
export type DockerImageRepositoryId = z.infer<typeof DockerImageRepositoryId>;

export const DockerImageTagId = z.string().uuid().brand("DockerImageTagId");
export type DockerImageTagId = z.infer<typeof DockerImageTagId>;

export const ComputeHeartbeatId = z.string().uuid().brand("ComputeHeartbeatId");
export type ComputeHeartbeatId = z.infer<typeof ComputeHeartbeatId>;

export const KernelId = z.string().brand("KernelId");
export type KernelId = z.infer<typeof KernelId>;

export const KernelSourceImageId = z
  .string()
  .uuid()
  .brand("KernelSourceImageId");
export type KernelSourceImageId = z.infer<typeof KernelSourceImageId>;

export const KernelSourceImageVersionId = z
  .string()
  .uuid()
  .brand("KernelSourceImageVersionId");
export type KernelSourceImageVersionId = z.infer<
  typeof KernelSourceImageVersionId
>;

export const LinkId = z.string().uuid().brand("LinkId");
export type LinkId = z.infer<typeof LinkId>;

export const MagicApiKeyId = z.string().uuid().brand("MagicApiKeyId");
export type MagicApiKeyId = z.infer<typeof MagicApiKeyId>;

export const MagicRulesFileId = z.string().uuid().brand("MagicRulesFileId");
export type MagicRulesFileId = z.infer<typeof MagicRulesFileId>;

export const MagicEventId = z.string().uuid().brand("MagicEventId");
export type MagicEventId = z.infer<typeof MagicEventId>;

export const SystemMagicEventId = z.string().uuid().brand("SystemMagicEventId");
export type SystemMagicEventId = z.infer<typeof SystemMagicEventId>;

export const MagicCompletionEventId = z
  .string()
  .uuid()
  .brand("MagicCompletionEventId");
export type MagicCompletionEventId = z.infer<typeof MagicCompletionEventId>;

export const LlmRequestLogId = z.string().uuid().brand("LlmRequestLogId");
export type LlmRequestLogId = z.infer<typeof LlmRequestLogId>;

export const MagicCompletionContextId = z
  .string()
  .uuid()
  .brand("MagicCompletionContextId");
export type MagicCompletionContextId = z.infer<typeof MagicCompletionContextId>;

export const MagicCellContentsId = z
  .string()
  .uuid()
  .brand("MagicCellContentsId");
export type MagicCellContentsId = z.infer<typeof MagicCellContentsId>;

export const MapCellId = z.string().uuid().brand("MapCellId");
export type MapCellId = z.infer<typeof MapCellId>;

export const MapLayerId = z.string().brand("MapLayer");
export type MapLayerId = z.infer<typeof MapLayerId>;

export const MarkdownCellId = z.string().uuid().brand("MarkdownCellId");
export type MarkdownCellId = z.infer<typeof MarkdownCellId>;

export const McpAppSwapTokenId = z.string().uuid().brand("McpAppSwapTokenId");
export type McpAppSwapTokenId = z.infer<typeof McpAppSwapTokenId>;

export const MetricCellId = z.string().uuid().brand("MetricCellId");
export type MetricCellId = z.infer<typeof MetricCellId>;

export const NotificationPreferencesId = z
  .string()
  .uuid()
  .brand("NotificationPreferencesId");
export type NotificationPreferencesId = z.infer<
  typeof NotificationPreferencesId
>;

export const NotionBotId = z.string().uuid().brand("NotionBotId");
export type NotionBotId = z.infer<typeof NotionBotId>;

export const NotionPreviewId = z.string().uuid().brand("NotionPreviewId");
export type NotionPreviewId = z.infer<typeof NotionPreviewId>;

export const OrgAppIconId = z.string().uuid().brand("OrgAppIconId");
export type OrgAppIconId = z.infer<typeof OrgAppIconId>;

export const OrgDomainId = z.string().uuid().brand("OrgDomainId");
export type OrgDomainId = z.infer<typeof OrgDomainId>;

export const OrgDomainVerificationLinkId = z
  .string()
  .uuid()
  .brand("OrgDomainVerificationLinkId");
export type OrgDomainVerificationLinkId = z.infer<
  typeof OrgDomainVerificationLinkId
>;

export const OrgId = z.string().brand("OrgId");
export type OrgId = z.infer<typeof OrgId>;

/**
 * A user-facing identifier for an org used to populate the URL
 * path. Interchangeable with {@link OrgId} in URLs and API calls, but is
 * a distinct, optional, set-once handle stored alongside the immutable `id`.
 */
export const OrgUserFacingId = z.string().brand("OrgUserFacingId");
export type OrgUserFacingId = z.infer<typeof OrgUserFacingId>;

export const McpServerConfigurationId = z
  .string()
  .uuid()
  .brand("McpServerConfigurationId");
export type McpServerConfigurationId = z.infer<typeof McpServerConfigurationId>;

export const OrgCustomFontId = z.string().uuid().brand("OrgCustomFontId");
export type OrgCustomFontId = z.infer<typeof OrgCustomFontId>;

export const OrgPublishedAppThemeId = z
  .string()
  .uuid()
  .brand("OrgPublishedAppThemeId");
export type OrgPublishedAppThemeId = z.infer<typeof OrgPublishedAppThemeId>;

export const OrgGuideFileId = z.string().uuid().brand("OrgGuideFileId");
export type OrgGuideFileId = z.infer<typeof OrgGuideFileId>;

export const OrgGuideFileVersionId = z
  .string()
  .uuid()
  .brand("OrgGuideFileVersionId");
export type OrgGuideFileVersionId = z.infer<typeof OrgGuideFileVersionId>;

export const OrgGuideFileVersionMentionId = z
  .string()
  .uuid()
  .brand("OrgGuideFileVersionMentionId");
export type OrgGuideFileVersionMentionId = z.infer<
  typeof OrgGuideFileVersionMentionId
>;

export const OrgGuideFilesAOLogId = z
  .string()
  .uuid()
  .brand("OrgGuideFilesAOLogId");
export type OrgGuideFilesAOLogId = z.infer<typeof OrgGuideFilesAOLogId>;

export const OrgGuideFilePath = z.string().brand("OrgGuideFilePath");
export type OrgGuideFilePath = z.infer<typeof OrgGuideFilePath>;

export const AgentResourceReferenceId = z
  .string()
  .uuid()
  .brand("AgentResourceReferenceId");
export type AgentResourceReferenceId = z.infer<typeof AgentResourceReferenceId>;

export const OutputContentId = z.string().uuid().brand("OutputContentId");
export type OutputContentId = z.infer<typeof OutputContentId>;

export const OutputContentKeyId = z.string().uuid().brand("OutputContentKeyId");
export type OutputContentKeyId = z.infer<typeof OutputContentKeyId>;

export const OutputContentParts = z.number().brand("OutputContentParts");
export type OutputContentParts = z.infer<typeof OutputContentParts>;

export const OutputContentPartNo = z.number().brand("OutputContentPartNo");
export type OutputContentPartNo = z.infer<typeof OutputContentPartNo>;

export const OutputId = z.string().uuid().brand("OutputId");
export type OutputId = z.infer<typeof OutputId>;

export const PackageId = z.string().brand("PackageId");
export type PackageId = z.infer<typeof PackageId>;

export const ParameterValueId = z.string().uuid().brand("ParameterValueId");
export type ParameterValueId = z.infer<typeof ParameterValueId>;

export const ParameterId = z.string().uuid().brand("ParameterId");
export type ParameterId = z.infer<typeof ParameterId>;

export const PrivilegeGrantee = z.string().brand("PrivilegeGrantee");
export type PrivilegeGrantee = z.infer<typeof PrivilegeGrantee>;

export const ParentDataConnectionId = z
  .string()
  .uuid()
  .brand("ParentDataConnectionId");
export type ParentDataConnectionId = z.infer<typeof ParentDataConnectionId>;

export const PinnedSchemaObjectId = z
  .string()
  .uuid()
  .brand("PinnedSchemaObjectId");
export type PinnedSchemaObjectId = z.infer<typeof PinnedSchemaObjectId>;

export const PivotCellId = z.string().uuid().brand("PivotCellId");
export type PivotCellId = z.infer<typeof PivotCellId>;

export const PresenceId = z.string().uuid().brand("PresenceId");
export type PresenceId = z.infer<typeof PresenceId>;

export const PresignedEmbeddingUrlId = z
  .string()
  .uuid()
  .brand("PresignedEmbeddingUrlId");
export type PresignedEmbeddingUrlId = z.infer<typeof PresignedEmbeddingUrlId>;

export const PaidComputeGrantId = z.string().uuid().brand("PaidComputeGrantId");
export type PaidComputeGrantId = z.infer<typeof PaidComputeGrantId>;

export const ProjectGrantId = z.string().uuid().brand("ProjectGrantId");
export type ProjectGrantId = z.infer<typeof ProjectGrantId>;

export const HexRetrievalEventId = z
  .string()
  .uuid()
  .brand("HexRetrievalEventId");
export type HexRetrievalEventId = z.infer<typeof HexRetrievalEventId>;

export const ProjectViewId = z.string().uuid().brand("ProjectViewId");
export type ProjectViewId = z.infer<typeof ProjectViewId>;

export const PromoId = z.string().uuid().brand("PromoId");
export type PromoId = z.infer<typeof PromoId>;

export const ExploreId = z.string().uuid().brand("ExploreId");
export type ExploreId = z.infer<typeof ExploreId>;

export const ExploreSessionStateId = z
  .string()
  .uuid()
  .brand("ExploreSessionStateId");
export type ExploreSessionStateId = z.infer<typeof ExploreSessionStateId>;

export const ReviewId = z.string().uuid().brand("ReviewId");
export type ReviewId = z.infer<typeof ReviewId>;

export const ReviewRequestId = z.string().uuid().brand("ReviewRequestId");
export type ReviewRequestId = z.infer<typeof ReviewRequestId>;

export const ReviewRequestLinkId = z
  .string()
  .uuid()
  .brand("ReviewRequestLinkId");
export type ReviewRequestLinkId = z.infer<typeof ReviewRequestLinkId>;

export const ReviewRequestAOLogId = z
  .string()
  .uuid()
  .brand("ReviewRequestAOLogId");
export type ReviewRequestAOLogId = z.infer<typeof ReviewRequestAOLogId>;

export const AgentResourceAccessLogId = z
  .string()
  .uuid()
  .brand("AgentResourceAccessLogId");
export type AgentResourceAccessLogId = z.infer<typeof AgentResourceAccessLogId>;

export const AgentAssumptionId = z.string().uuid().brand("AgentAssumptionId");
export type AgentAssumptionId = z.infer<typeof AgentAssumptionId>;

export const AgentAssumptionKnowledgeLinkId = z
  .string()
  .uuid()
  .brand("AgentAssumptionKnowledgeLinkId");
export type AgentAssumptionKnowledgeLinkId = z.infer<
  typeof AgentAssumptionKnowledgeLinkId
>;

export const AgentAssumptionResourceLinkId = z
  .string()
  .uuid()
  .brand("AgentAssumptionResourceLinkId");
export type AgentAssumptionResourceLinkId = z.infer<
  typeof AgentAssumptionResourceLinkId
>;

export const KnowledgeAtomId = z.string().uuid().brand("KnowledgeAtomId");
export type KnowledgeAtomId = z.infer<typeof KnowledgeAtomId>;

export const KnowledgeAtomAccessLogId = z
  .string()
  .uuid()
  .brand("KnowledgeAtomAccessLogId");
export type KnowledgeAtomAccessLogId = z.infer<typeof KnowledgeAtomAccessLogId>;

export const KnowledgeGraphId = z.string().uuid().brand("KnowledgeGraphId");
export type KnowledgeGraphId = z.infer<typeof KnowledgeGraphId>;

export const KnowledgeAtomResourceLinkId = z
  .string()
  .uuid()
  .brand("KnowledgeAtomResourceLinkId");
export type KnowledgeAtomResourceLinkId = z.infer<
  typeof KnowledgeAtomResourceLinkId
>;

export const AgentAssumptionResolutionId = z
  .string()
  .uuid()
  .brand("AgentAssumptionResolutionId");
export type AgentAssumptionResolutionId = z.infer<
  typeof AgentAssumptionResolutionId
>;

export const KnowledgeCurationRunId = z
  .string()
  .uuid()
  .brand("KnowledgeCurationRunId");
export type KnowledgeCurationRunId = z.infer<typeof KnowledgeCurationRunId>;

export const KnowledgeCurationResourceStateId = z
  .string()
  .uuid()
  .brand("KnowledgeCurationResourceStateId");
export type KnowledgeCurationResourceStateId = z.infer<
  typeof KnowledgeCurationResourceStateId
>;

export const SemanticProjectVersionAOLogId = z
  .string()
  .uuid()
  .brand("SemanticProjectVersionAOLogId");
export type SemanticProjectVersionAOLogId = z.infer<
  typeof SemanticProjectVersionAOLogId
>;

export const ScheduleNotificationRecipientId = z
  .string()
  .uuid()
  .brand("ScheduleNotificationRecipientId");
export type ScheduleNotificationRecipientId = z.infer<
  typeof ScheduleNotificationRecipientId
>;

export const ScopeItemId = z.string().uuid().brand("ScopeItemId");
export type ScopeItemId = z.infer<typeof ScopeItemId>;

export const SearchHexesResultId = z.string().brand("SearchHexesResultId");
export type SearchHexesResultId = z.infer<typeof SearchHexesResultId>;

export const AgentThreadSearchResultId = z
  .string()
  .brand("AgentThreadSearchResultId");
export type AgentThreadSearchResultId = z.infer<
  typeof AgentThreadSearchResultId
>;

export const SecretId = z.string().uuid().brand("SecretId");
export type SecretId = z.infer<typeof SecretId>;

export const SharedFilterId = z.string().uuid().brand("SharedFilterId");
export type SharedFilterId = z.infer<typeof SharedFilterId>;

export const SharedFilterSessionStateId = z
  .string()
  .uuid()
  .brand("SharedFilterSessionStateId");
export type SharedFilterSessionStateId = z.infer<
  typeof SharedFilterSessionStateId
>;

export const SharedAssetGrantId = z.string().uuid().brand("SharedAssetGrantId");
export type SharedAssetGrantId = z.infer<typeof SharedAssetGrantId>;

export const SlackAuthorizationId = z
  .string()
  .uuid()
  .brand("SlackAuthorizationId");
export type SlackAuthorizationId = z.infer<typeof SlackAuthorizationId>;

export const SnowflakeOAuthConnectionId = z
  .string()
  .uuid()
  .brand("SnowflakeOAuthConnectionId");
export type SnowflakeOAuthConnectionId = z.infer<
  typeof SnowflakeOAuthConnectionId
>;

export const SnowflakeOAuthRolePreferenceId = z
  .string()
  .uuid()
  .brand("SnowflakeOAuthRolePreferenceId");
export type SnowflakeOAuthRolePreferenceId = z.infer<
  typeof SnowflakeOAuthRolePreferenceId
>;

export const SqlCellId = z.string().uuid().brand("SqlCellId");
export type SqlCellId = z.infer<typeof SqlCellId>;

export const SqlConfigId = z.string().uuid().brand("SqlConfigId");
export type SqlConfigId = z.infer<typeof SqlConfigId>;

export const SemanticRetrievalEventId = z
  .string()
  .uuid()
  .brand("SemanticRetrievalEventId");
export type SemanticRetrievalEventId = z.infer<typeof SemanticRetrievalEventId>;

export const SshKeyId = z.string().uuid().brand("SshKeyId");
export type SshKeyId = z.infer<typeof SshKeyId>;

export const StaticCellId = z.string().uuid().brand("StaticCellId");
export type StaticCellId = z.infer<typeof StaticCellId>;

export const StaticDataConnectionId = z
  .string()
  .uuid()
  .brand("StaticDataConnectionId");
export type StaticDataConnectionId = z.infer<typeof StaticDataConnectionId>;

export const StaticSharedFilterId = z
  .string()
  .uuid()
  .brand("StaticSharedFilterId");
export type StaticSharedFilterId = z.infer<typeof StaticSharedFilterId>;

export const StatusId = z.string().uuid().brand("StatusId");
export type StatusId = z.infer<typeof StatusId>;

export const StoryElementId = z.string().uuid().brand("StoryElementId");
export type StoryElementId = z.infer<typeof StoryElementId>;

export const StoryLayoutId = z.string().uuid().brand("StoryLayoutId");
export type StoryLayoutId = z.infer<typeof StoryLayoutId>;

export const SyncRepositoryHexVersionLinkId = z
  .string()
  .uuid()
  .brand("SyncRepositoryHexVersionLinkId");
export type SyncRepositoryHexVersionLinkId = z.infer<
  typeof SyncRepositoryHexVersionLinkId
>;

export const SyncRepositoryId = z.string().uuid().brand("SyncRepositoryId");
export type SyncRepositoryId = z.infer<typeof SyncRepositoryId>;

export const TaskId = z.string().uuid().brand("TaskId");
export type TaskId = z.infer<typeof TaskId>;

export const TaskConfigId = z.string().uuid().brand("TaskConfigId");
export type TaskConfigId = z.infer<typeof TaskConfigId>;

export const TaskRecipientId = z.string().uuid().brand("TaskRecipientId");
export type TaskRecipientId = z.infer<typeof TaskRecipientId>;

export const TextCellId = z.string().uuid().brand("TextCellId");
export type TextCellId = z.infer<typeof TextCellId>;

export const UILockId = z.string().uuid().brand("UILockId");
export type UILockId = z.infer<typeof UILockId>;

export const UserGroupLinkId = z.string().uuid().brand("UserGroupLinkId");
export type UserGroupLinkId = z.infer<typeof UserGroupLinkId>;

export const UserId = z.string().uuid().brand("UserId");
export type UserId = z.infer<typeof UserId>;

export const UserMcpServerConnectionId = z
  .string()
  .uuid()
  .brand("UserMcpServerConnectionId");
export type UserMcpServerConnectionId = z.infer<
  typeof UserMcpServerConnectionId
>;

export const ServerId = z.literal("SERVER_API_CLIENT");
export type ServerId = z.infer<typeof ServerId>;

export const AiAgentId = z.string().brand("AiAgentId");
export type AiAgentId = z.infer<typeof AiAgentId>;

export const ClientId = z.union([UserId, ApiClientId, ServerId, AiAgentId]);
export type ClientId = z.infer<typeof ClientId>;

export const ExecutionEventId = z.string().uuid().brand("ExecutionEventId");
export type ExecutionEventId = z.infer<typeof ExecutionEventId>;

export const ProjectStateId = z.string().uuid().brand("ProjectStateId");
export type ProjectStateId = z.infer<typeof ProjectStateId>;

export const ProjectRunMetadataId = z
  .string()
  .uuid()
  .brand("ProjectRunMetadataId");
export type ProjectRunMetadataId = z.infer<typeof ProjectRunMetadataId>;

export const UserSnowflakeOAuthConnectionId = z
  .string()
  .uuid()
  .brand("UserSnowflakeOAuthConnectionId");
export type UserSnowflakeOAuthConnectionId = z.infer<
  typeof UserSnowflakeOAuthConnectionId
>;

export const UserBigQueryOAuthConnectionId = z
  .string()
  .uuid()
  .brand("UserBigQueryOAuthConnectionId");
export type UserBigQueryOAuthConnectionId = z.infer<
  typeof UserBigQueryOAuthConnectionId
>;

export const UserDatabricksOAuthConnectionId = z
  .string()
  .uuid()
  .brand("UserDatabricksOAuthConnectionId");
export type UserDatabricksOAuthConnectionId = z.infer<
  typeof UserDatabricksOAuthConnectionId
>;

export const UserMCPApplicationId = z.string().brand("UserMCPApplicationId");
export type UserMCPApplicationId = z.infer<typeof UserMCPApplicationId>;

export const UserMemoryId = z.string().uuid().brand("UserMemoryId");
export type UserMemoryId = z.infer<typeof UserMemoryId>;
export const UserMemoryVersionId = z
  .string()
  .uuid()
  .brand("UserMemoryVersionId");
export type UserMemoryVersionId = z.infer<typeof UserMemoryVersionId>;
export const UserMemoryExtractionRunId = z
  .string()
  .uuid()
  .brand("UserMemoryExtractionRunId");
export type UserMemoryExtractionRunId = z.infer<
  typeof UserMemoryExtractionRunId
>;

export const UserStarredHexLinkId = z.string().brand("UserStarredHexLinkId");
export type UserStarredHexLinkId = z.infer<typeof UserStarredHexLinkId>;

export const UserStarredCollectionLinkId = z
  .string()
  .uuid()
  .brand("UserStarredCollectionLinkId");
export type UserStarredCollectionLinkId = z.infer<
  typeof UserStarredCollectionLinkId
>;

export const VcsAccessTokenId = z.string().uuid().brand("VcsAccessTokenId");
export type VcsAccessTokenId = z.infer<typeof VcsAccessTokenId>;

export const VcsConnectionId = z.string().uuid().brand("VcsConnectionId");
export type VcsConnectionId = z.infer<typeof VcsConnectionId>;

export const VcsPackageId = z.string().uuid().brand("VcsPackageId");
export type VcsPackageId = z.infer<typeof VcsPackageId>;

export const VcsPackageHexVersionLinkId = z
  .string()
  .uuid()
  .brand("VcsPackageHexVersionLinkId");
export type VcsPackageHexVersionLinkId = z.infer<
  typeof VcsPackageHexVersionLinkId
>;

export const VegaChartCellId = z.string().uuid().brand("VegaChartCellId");
export type VegaChartCellId = z.infer<typeof VegaChartCellId>;

export const WritebackCellId = z.string().uuid().brand("WritebackCellId");
export type WritebackCellId = z.infer<typeof WritebackCellId>;

export const CustomCodeFormattingConfigId = z
  .string()
  .uuid()
  .brand("CustomCodeFormattingConfigId");
export type CustomCodeFormattingConfigId = z.infer<
  typeof CustomCodeFormattingConfigId
>;

export const CustomSuggestionsConfigId = z
  .string()
  .uuid()
  .brand("CustomSuggestionsConfigId");
export type CustomSuggestionsConfigId = z.infer<
  typeof CustomSuggestionsConfigId
>;

export const TableQueryEventId = z.string().uuid().brand("TableQueryEventId");
export type TableQueryEventId = z.infer<typeof TableQueryEventId>;

export const TableCoocQueryEventId = z
  .string()
  .uuid()
  .brand("TableCoocQueryEventId");
export type TableCoocQueryEventId = z.infer<typeof TableCoocQueryEventId>;

export const LanceTableOfDataSourceColumnsId = z
  .string()
  .uuid()
  .brand("LanceTableOfDataSourceColumnsId");
export type LanceTableOfDataSourceColumnsId = z.infer<
  typeof LanceTableOfDataSourceColumnsId
>;

export const LanceTableOfHexesId = z
  .string()
  .uuid()
  .brand("LanceTableOfHexesId");
export type LanceTableOfHexesId = z.infer<typeof LanceTableOfHexesId>;

export const LanceTableReferenceId = z
  .string()
  .uuid()
  .brand("LanceTableReferenceId");
export type LanceTableReferenceId = z.infer<typeof LanceTableReferenceId>;

export const LanceTableReferenceConstraintId = z
  .string()
  .uuid()
  .brand("LanceTableReferenceConstraintId");
export type LanceTableReferenceConstraintId = z.infer<
  typeof LanceTableReferenceConstraintId
>;

export const SchemaRefreshRequestId = z
  .string()
  .uuid()
  .brand("SchemaRefreshRequestId");
export type SchemaRefreshRequestId = z.infer<typeof SchemaRefreshRequestId>;

export const SchemaRefreshEntityDiffId = z
  .string()
  .uuid()
  .brand("SchemaRefreshEntityDiffId");
export type SchemaRefreshEntityDiffId = z.infer<
  typeof SchemaRefreshEntityDiffId
>;

export const LanceTableActionId = z.string().uuid().brand("LanceTableActionId");
export type LanceTableActionId = z.infer<typeof LanceTableActionId>;

export const GridElementEntityId = z.union([CellId, SharedFilterId]);
export type GridElementEntityId = z.infer<typeof GridElementEntityId>;

export const NotificationUserLinkId = z
  .string()
  .uuid()
  .brand("NotificationUserLinkId");
export type NotificationUserLinkId = z.infer<typeof NotificationUserLinkId>;

export const NotificationId = z.string().uuid().brand("NotificationId");
export type NotificationId = z.infer<typeof NotificationId>;

export const K8sResourceId = z.string().brand("K8sResourceId");
export type K8sResourceId = z.infer<typeof K8sResourceId>;

export const SemanticProjectId = z.string().uuid().brand("SemanticProjectId");
export type SemanticProjectId = z.infer<typeof SemanticProjectId>;

export const SemanticProjectSqlId = z.string().brand("SemanticProjectSqlId");
export type SemanticProjectSqlId = z.infer<typeof SemanticProjectSqlId>;

export const TaoProjectId = z.string().uuid().brand("TaoProjectId");
export type TaoProjectId = z.infer<typeof TaoProjectId>;

export const TaoProjectAgentChatThreadLinkId = z
  .string()
  .uuid()
  .brand("TaoProjectAgentChatThreadLinkId");
export type TaoProjectAgentChatThreadLinkId = z.infer<
  typeof TaoProjectAgentChatThreadLinkId
>;

export const AgentChatThreadLinkId = z
  .string()
  .uuid()
  .brand("AgentChatThreadLinkId");
export type AgentChatThreadLinkId = z.infer<typeof AgentChatThreadLinkId>;

export const DataConnectionTaoProjectLinkId = z
  .string()
  .uuid()
  .brand("DataConnectionTaoProjectLinkId");
export type DataConnectionTaoProjectLinkId = z.infer<
  typeof DataConnectionTaoProjectLinkId
>;

export const TaoPodId = z.string().uuid().brand("TaoPodId");
export type TaoPodId = z.infer<typeof TaoPodId>;

export const TaoAppId = z.string().uuid().brand("TaoAppId");
export type TaoAppId = z.infer<typeof TaoAppId>;

export const TaoAppPodBindingId = z.string().uuid().brand("TaoAppPodBindingId");
export type TaoAppPodBindingId = z.infer<typeof TaoAppPodBindingId>;

export const TaoProjectSourceFileId = z
  .string()
  .uuid()
  .brand("TaoProjectSourceFileId");
export type TaoProjectSourceFileId = z.infer<typeof TaoProjectSourceFileId>;

export const TaoProjectAOLogId = z.string().uuid().brand("TaoProjectAOLogId");
export type TaoProjectAOLogId = z.infer<typeof TaoProjectAOLogId>;

export const TaoProjectGrantId = z.string().uuid().brand("TaoProjectGrantId");
export type TaoProjectGrantId = z.infer<typeof TaoProjectGrantId>;

/**
 * Opaque identifier for a plain-code workload pod. Owned by the pod team.
 * Callers construct this via `workloadPodKeyForBranch(branchId)` or
 * similar helpers; the service layer never inspects its contents.
 */
export const WorkloadPodKey = z.string().brand("WorkloadPodKey");
export type WorkloadPodKey = z.infer<typeof WorkloadPodKey>;

export const EditSessionBranchId = z
  .string()
  .uuid()
  .brand("EditSessionBranchId");
export type EditSessionBranchId = z.infer<typeof EditSessionBranchId>;

export const WorkloadPodBranchBindingId = z
  .string()
  .uuid()
  .brand("WorkloadPodBranchBindingId");
export type WorkloadPodBranchBindingId = z.infer<
  typeof WorkloadPodBranchBindingId
>;

export const SemanticProjectVersionId = z
  .string()
  .uuid()
  .brand("SemanticProjectVersionId");
export type SemanticProjectVersionId = z.infer<typeof SemanticProjectVersionId>;

export const SemanticProjectVersionBuildId = z
  .string()
  .uuid()
  .brand("SemanticProjectVersionBuildId");
export type SemanticProjectVersionBuildId = z.infer<
  typeof SemanticProjectVersionBuildId
>;

export const SemanticProjectFileId = z
  .string()
  .uuid()
  .brand("SemanticProjectFileId");
export type SemanticProjectFileId = z.infer<typeof SemanticProjectFileId>;

export const SemanticProjectFilePath = z
  .string()
  .brand("SemanticProjectFilePath");
export type SemanticProjectFilePath = z.infer<typeof SemanticProjectFilePath>;

export const SemanticDatasetId = z.string().uuid().brand("SemanticDatasetId");
export type SemanticDatasetId = z.infer<typeof SemanticDatasetId>;

export const SemanticDatasetName = z.string().brand("SemanticDatasetName");
/**
 * Identifier for a semantic dataset name *or* a
 * user-joined table stored in `someExploreSpec.tables`
 * (since at computation-time these are treated like semantic datasets)
 */
export type SemanticDatasetName = z.infer<typeof SemanticDatasetName>;

/** Globally unique primary key for a persisted semantic view. Not stable across versions. */
export type SemanticViewId = z.infer<typeof SemanticViewId>;
export const SemanticViewId = z.string().uuid().brand("SemanticViewId");

/** Stable identifier for a semantic view across versions. Unique within a project version. */
export type SemanticViewName = z.infer<typeof SemanticViewName>;
export const SemanticViewName = z.string().brand("SemanticViewName");

export const SemanticObjectCategoryLinkId = z
  .string()
  .uuid()
  .brand("SemanticObjectCategoryLinkId");
export type SemanticObjectCategoryLinkId = z.infer<
  typeof SemanticObjectCategoryLinkId
>;

export const SemanticDatasetStatusLinkId = z
  .string()
  .uuid()
  .brand("SemanticDatasetStatusLinkId");
export type SemanticDatasetStatusLinkId = z.infer<
  typeof SemanticDatasetStatusLinkId
>;

export const SemanticViewStatusLinkId = z
  .string()
  .uuid()
  .brand("SemanticViewStatusLinkId");
export type SemanticViewStatusLinkId = z.infer<typeof SemanticViewStatusLinkId>;

export const UserToGranteesId = z.string().uuid().brand("UserToGranteesId");
export type UserToGranteesId = z.infer<typeof UserToGranteesId>;

export const CollapsibleCellId = z.string().uuid().brand("CollapsibleCellId");
export type CollapsibleCellId = z.infer<typeof CollapsibleCellId>;

export const ComponentReferenceId = z.string().brand("ComponentReferenceId");
export type ComponentReferenceId = z.infer<typeof ComponentReferenceId>;

export const SemanticDatasetDataSourceTableLinkId = z
  .string()
  .uuid()
  .brand("SemanticDatasetDataSourceTableLinkId");
export type SemanticDatasetDataSourceTableLinkId = z.infer<
  typeof SemanticDatasetDataSourceTableLinkId
>;

export const DocusignEnvelopeId = z.string().brand("DocusignEnvelopeId");
export type DocusignEnvelopeId = z.infer<typeof DocusignEnvelopeId>;

export const DocusignTemplateId = z.string().brand("DocusignTemplateId");
export type DocusignTemplateId = z.infer<typeof DocusignTemplateId>;

export const ReceivedDocusignEventId = z
  .string()
  .uuid()
  .brand("ReceivedDocusignEventId");
export type ReceivedDocusignEventId = z.infer<typeof ReceivedDocusignEventId>;

export const BullQueueJobId = z.string().brand("BullQueueJobId");
export type BullQueueJobId = z.infer<typeof BullQueueJobId>;

export const TableReferenceId = z.string().uuid().brand("TableReferenceId");
export type TableReferenceId = z.infer<typeof TableReferenceId>;

export const TableEntryId = z.string().brand("TableEntryId");
export type TableEntryId = z.infer<typeof TableEntryId>;

export const ReceivedStripeEventId = z.string().brand("ReceivedStripeEventId");
export type ReceivedStripeEventId = z.infer<typeof ReceivedStripeEventId>;

export const ReceivedWorkOSEventId = z.string().brand("ReceivedWorkOSEventId");
export type ReceivedWorkOSEventId = z.infer<typeof ReceivedWorkOSEventId>;

export const QueryCacheEntryId = z.string().uuid().brand("QueryCacheEntryId");
export type QueryCacheEntryId = z.infer<typeof QueryCacheEntryId>;

export const HeartbeatId = z.string().brand("HeartbeatId");
export type HeartbeatId = z.infer<typeof HeartbeatId>;

export const HeroClusterId = z.string().uuid().brand("HeroClusterId");
export type HeroClusterId = z.infer<typeof HeroClusterId>;

export const HeroFindingId = z.string().uuid().brand("HeroFindingId");
export type HeroFindingId = z.infer<typeof HeroFindingId>;

export const TableDeletionQueueId = z
  .string()
  .uuid()
  .brand("TableDeletionQueueId");
export type TableDeletionQueueId = z.infer<typeof TableDeletionQueueId>;

export const DataSourceShardDeletionQueueEventType = z.enum([
  "CATEGORY_DELETED",
  "DATA_CONNECTION_DELETED",
  "STATUS_DELETED",
]);
export type DataSourceShardDeletionQueueEventType = z.infer<
  typeof DataSourceShardDeletionQueueEventType
>;

export const DataSourceShardDeletionQueueId = z
  .string()
  .uuid()
  .brand("DataSourceShardDeletionQueueId");
export type DataSourceShardDeletionQueueId = z.infer<
  typeof DataSourceShardDeletionQueueId
>;

export const FileDeletionQueueId = z
  .string()
  .uuid()
  .brand("FileDeletionQueueId");
export type FileDeletionQueueId = z.infer<typeof FileDeletionQueueId>;

export const OutputContentDeletionQueueId = z
  .string()
  .uuid()
  .brand("OutputContentDeletionQueueId");
export type OutputContentDeletionQueueId = z.infer<
  typeof OutputContentDeletionQueueId
>;

export const AgentChatThreadId = z.string().uuid().brand("AgentChatThreadId");
export type AgentChatThreadId = z.infer<typeof AgentChatThreadId>;

export const AgentCellEditId = z.string().uuid().brand("AgentCellEditId");
export type AgentCellEditId = z.infer<typeof AgentCellEditId>;

export const AgentSemanticProjectFileEditId = z
  .string()
  .uuid()
  .brand("AgentSemanticProjectFileEditId");
export type AgentSemanticProjectFileEditId = z.infer<
  typeof AgentSemanticProjectFileEditId
>;

export const AgentChatMessageClassificationId = z
  .string()
  .uuid()
  .brand("AgentChatMessageClassificationId");
export type AgentChatMessageClassificationId = z.infer<
  typeof AgentChatMessageClassificationId
>;

export const AgentChatMessageId = z.string().uuid().brand("AgentChatMessageId");
export type AgentChatMessageId = z.infer<typeof AgentChatMessageId>;

export const AgentChatQueuedPromptId = z
  .string()
  .uuid()
  .brand("AgentChatQueuedPromptId");
export type AgentChatQueuedPromptId = z.infer<typeof AgentChatQueuedPromptId>;

export const StaticAgentChatMessageId = z
  .string()
  .uuid()
  .brand("StaticAgentChatMessageId");
export type StaticAgentChatMessageId = z.infer<typeof StaticAgentChatMessageId>;

export const AgentChatRunId = z.string().uuid().brand("AgentChatRunId");
export type AgentChatRunId = z.infer<typeof AgentChatRunId>;

export const AgentFeedbackId = z.string().uuid().brand("AgentFeedbackId");
export type AgentFeedbackId = z.infer<typeof AgentFeedbackId>;

export const ThreadExtractionId = z.string().uuid().brand("ThreadExtractionId");
export type ThreadExtractionId = z.infer<typeof ThreadExtractionId>;

export const ThreadSummaryId = z.string().uuid().brand("ThreadSummaryId");
export type ThreadSummaryId = z.infer<typeof ThreadSummaryId>;

export const ThreadConfusionId = z.string().uuid().brand("ThreadConfusionId");
export type ThreadConfusionId = z.infer<typeof ThreadConfusionId>;

export const ThreadRetrievalFailureId = z
  .string()
  .uuid()
  .brand("ThreadRetrievalFailureId");
export type ThreadRetrievalFailureId = z.infer<typeof ThreadRetrievalFailureId>;

export const ThreadTopicId = z.string().uuid().brand("ThreadTopicId");
export type ThreadTopicId = z.infer<typeof ThreadTopicId>;

export const ThreadTopicAssignmentId = z
  .string()
  .uuid()
  .brand("ThreadTopicAssignmentId");
export type ThreadTopicAssignmentId = z.infer<typeof ThreadTopicAssignmentId>;

export const BillingCycleAiBillingUsageAggregationId = z
  .string()
  .uuid()
  .brand("BillingCycleAiBillingUsageAggregationId");
export type BillingCycleAiBillingUsageAggregationId = z.infer<
  typeof BillingCycleAiBillingUsageAggregationId
>;

export const BillingCycleCreditGrantConsumptionAggregationId = z
  .string()
  .uuid()
  .brand("BillingCycleCreditGrantConsumptionAggregationId");
export type BillingCycleCreditGrantConsumptionAggregationId = z.infer<
  typeof BillingCycleCreditGrantConsumptionAggregationId
>;

export const AiBillingUsageId = z.string().uuid().brand("AiBillingUsageId");
export type AiBillingUsageId = z.infer<typeof AiBillingUsageId>;

export const AiUsageSettingsId = z.string().uuid().brand("AiUsageSettingsId");
export type AiUsageSettingsId = z.infer<typeof AiUsageSettingsId>;

export const CreditGrantId = z.string().uuid().brand("CreditGrantId");
export type CreditGrantId = z.infer<typeof CreditGrantId>;

export const CreditGrantConsumptionId = z
  .string()
  .uuid()
  .brand("CreditGrantConsumptionId");
export type CreditGrantConsumptionId = z.infer<typeof CreditGrantConsumptionId>;

export const CreditGrantConsumptionLogId = z
  .string()
  .uuid()
  .brand("CreditGrantConsumptionLogId");
export type CreditGrantConsumptionLogId = z.infer<
  typeof CreditGrantConsumptionLogId
>;

export const UserToHubspotContactId = z
  .string()
  .uuid()
  .brand("UserToHubspotContactId");
export type UserToHubspotContactId = z.infer<typeof UserToHubspotContactId>;

export const HubspotContactId = z.string().brand("HubspotContactId");
export type HubspotContactId = z.infer<typeof HubspotContactId>;

export const HubspotAnonymousId = z.string().brand("HubspotAnonymousId");
export type HubspotAnonymousId = z.infer<typeof HubspotAnonymousId>;

export const ContextSuggestionSourceId = z
  .string()
  .uuid()
  .brand("ContextSuggestionSourceId");
export type ContextSuggestionSourceId = z.infer<
  typeof ContextSuggestionSourceId
>;

export const ContextSuggestionIssueId = z
  .string()
  .uuid()
  .brand("ContextSuggestionIssueId");
export type ContextSuggestionIssueId = z.infer<typeof ContextSuggestionIssueId>;

export const ContextSuggestionRunId = z
  .string()
  .uuid()
  .brand("ContextSuggestionRunId");
export type ContextSuggestionRunId = z.infer<typeof ContextSuggestionRunId>;

export const ContextSuggestionFileUpdateId = z
  .string()
  .uuid()
  .brand("ContextSuggestionFileUpdateId");
export type ContextSuggestionFileUpdateId = z.infer<
  typeof ContextSuggestionFileUpdateId
>;

export const ContextSuggestionDataWarehouseUpdateId = z
  .string()
  .uuid()
  .brand("ContextSuggestionDataWarehouseUpdateId");
export type ContextSuggestionDataWarehouseUpdateId = z.infer<
  typeof ContextSuggestionDataWarehouseUpdateId
>;

export const ContextChangesetId = z.string().uuid().brand("ContextChangesetId");
export type ContextChangesetId = z.infer<typeof ContextChangesetId>;

export const ContextVersionId = z.string().uuid().brand("ContextVersionId");
export type ContextVersionId = z.infer<typeof ContextVersionId>;

/**
 * Represents any possible database entity id in our codebase
 */
export type DataBaseEntityId =
  | AppSessionAOLogId
  | AppSessionCellId
  | AppSessionEventLogId
  | AppSessionFileId
  | AppSessionGrantId
  | AppSessionId
  | AppSessionDeletionEventId
  | AiBillingUsageId
  | AiUsageSettingsId
  | ApiClientId
  | AgentChatThreadId
  | AgentCellEditId
  | AgentSemanticProjectFileEditId
  | AgentChatMessageClassificationId
  | AgentChatMessageId
  | AgentChatQueuedPromptId
  | AgentFeedbackId
  | BigQueryOAuthConnectionId
  | BlockCellId
  | CanvasElementId
  | CanvasLayoutId
  | DataAppId
  | DataAppElementId
  | DataAppFileAOLogId
  | DataAppFileId
  | CategoryId
  | CellGroupCellId
  | CellId
  | ChartCellId
  | ChartCellSessionStateId
  | CodeCellId
  | CollectionGrantId
  | CollectionHexLinkId
  | CollectionId
  | ColumnPropertiesId
  | ColorPaletteId
  | CompactedAppSessionEventLogId
  | ComputeHeartbeatId
  | CustomCodeFormattingConfigId
  | CustomSuggestionsConfigId
  | CommentId
  | CommentReactionId
  | ComponentImportCellId
  | ComputeActionId
  | CreditGrantId
  | DatabricksOAuthConnectionId
  | DataConnectionSchemaRefreshId
  | DataConnectionSchemaRefreshHistoryId
  | DataConnectionSchemaRefreshScheduleId
  | DataConnectionHexVersionLinkId
  | DataConnectionId
  | DataConnectionOAuthSettingsId
  | DataSourceColumnId
  | DataSourceColumnMetadataId
  | DataSourceDatabaseId
  | DataSourceDatabaseMetadataId
  | DataSourceDbtMetricId
  | DataSourceGrantId
  | DataSourceSchemaId
  | DataSourceSchemaMetadataId
  | DbtMetricCellDataSourceDbtMetricLinkId
  | DbtMetricCellId
  | DeviceCodeId
  | DataSourceTableId
  | DataSourceTableMetadataId
  | DataSourceObjectCategoryLinkId
  | DisplayTableCellId
  | DisplayTableConfigId
  | DisplayTableSessionStateId
  | DomainDenialId
  | EmailDenialId
  | EmailVerificationLinkId
  | EvalBaseId
  | CustomerEvalGradeResultId
  | EvalResultId
  | EvalRubricId
  | EvalRunId
  | EvalSetId
  | EvalSetRunId
  | EvalSemanticProjectFileId
  | ExploreCellId
  | ExploreSpecSessionStateId
  | ExternalContextConnectionId
  | K8sResourceId
  | EmbeddedApiCallId
  | EmbeddedOAuthCredentialsId
  | EnvVarId
  | ExecutionEventId
  | ExternalFileIntegrationId
  | ExternalFileIntegrationHexVersionLinkId
  | ExploreAgentSettingsId
  | FileHexVersionLinkId
  | FileId
  | FolderEntryId
  | FolderEntryGrantId
  | FilterCellId
  | FilterCellSessionStateId
  | GithubServerConfigId
  | GoogleDriveOauthAccountId
  | GridElementId
  | GridColumnId
  | GridLayoutId
  | GridRowId
  | GroupId
  | HexAnalyticsId
  | SystemMagicEventId
  | MagicRulesFileId
  | HexId
  | HexAOLogId
  | HexCategoryLinkId
  | TaoCategoryLinkId
  | HexRetrievalEventId
  | HexVersionOAuthSettingsId
  | HexVersionDefaultAppSessionId
  | HexScheduleId
  | HexVersionAOLogId
  | HexVersionId
  | HexVersionSecretId
  | DockerImageRepositoryId
  | DockerImageTagId
  | KernelSourceImageId
  | KernelSourceImageVersionId
  | KernelSourceImageVersionId
  | LanceTableActionId
  | LanceTableOfDataSourceColumnsId
  | LanceTableOfHexesId
  | LanceTableReferenceId
  | LanceTableReferenceConstraintId
  | LlmRequestLogId
  | LinkId
  | MagicApiKeyId
  | MagicCellContentsId
  | MagicCompletionContextId
  | MagicCompletionEventId
  | MagicEventId
  | MapCellId
  | MarkdownCellId
  | MetricCellId
  | McpAppSwapTokenId
  | NotificationPreferencesId
  | NotionBotId
  | NotionPreviewId
  | ThreadExtractionId
  | ThreadSummaryId
  | ThreadConfusionId
  | ThreadRetrievalFailureId
  | ThreadTopicId
  | ThreadTopicAssignmentId
  | ThreadExtractionEvalResultId
  | ThreadExtractionExperimentId
  | OrgAppIconId
  | OrgCustomFontId
  | OrgDomainId
  | OrgDomainVerificationLinkId
  | OrgId
  | OrgPublishedAppThemeId
  | OrgGuideFileId
  | OrgGuideFileVersionId
  | OrgGuideFileVersionMentionId
  | OrgGuideFilesAOLogId
  | AgentResourceReferenceId
  | OutputContentId
  | OutputId
  | ParameterId
  | ParameterValueId
  | PinnedSchemaObjectId
  | PivotCellId
  | PresenceId
  | PresignedEmbeddingUrlId
  | PaidComputeGrantId
  | ProjectGrantId
  | ProjectViewId
  | ProjectStateId
  | ProjectRunMetadataId
  | PromoId
  | ReviewId
  | ReviewRequestId
  | ReviewRequestLinkId
  | AgentResourceAccessLogId
  | AgentAssumptionId
  | AgentAssumptionKnowledgeLinkId
  | AgentAssumptionResourceLinkId
  | KnowledgeAtomId
  | KnowledgeAtomAccessLogId
  | KnowledgeGraphId
  | KnowledgeAtomResourceLinkId
  | AgentAssumptionResolutionId
  | KnowledgeCurationRunId
  | KnowledgeCurationResourceStateId
  | ReviewRequestAOLogId
  | ScheduleNotificationRecipientId
  | ScopeItemId
  | SemanticRetrievalEventId
  | SecretId
  | SemanticProjectVersionAOLogId
  | SharedFilterId
  | SharedFilterSessionStateId
  | SharedAssetGrantId
  | SlackAgentChatThreadLinkId
  | SlackAuthorizationId
  | SnowflakeOAuthConnectionId
  | SnowflakeOAuthRolePreferenceId
  | SqlCellId
  | SshKeyId
  | StatusId
  | StoryElementId
  | StoryLayoutId
  | StripePriceId
  | StripeProductId
  | StripeSubscriptionId
  | StripeSubscriptionItemId
  | SyncRepositoryHexVersionLinkId
  | SyncRepositoryHexVersionLinkId
  | SyncRepositoryId
  | TaskId
  | TaskConfigId
  | TaskRecipientId
  | TextCellId
  | UILockId
  | UserGroupLinkId
  | UserId
  | UserBigQueryOAuthConnectionId
  | UserDatabricksOAuthConnectionId
  | UserSnowflakeOAuthConnectionId
  | UserStarredHexLinkId
  | UserStarredCollectionLinkId
  | UserMemoryId
  | UserMemoryVersionId
  | UserMemoryExtractionRunId
  | VcsConnectionId
  | VcsPackageId
  | VcsPackageHexVersionLinkId
  | VcsPackageHexVersionLinkId
  | VegaChartCellId
  | WritebackCellId
  | ScheduleNotificationId
  | ScheduleNotificationExportId
  | ScheduleNotificationConditionId
  | SlackChannelId
  | SlackTeamAuthorizationId
  | SlackUserId
  | RunNotificationRecipientId
  | ExploreId
  | ExploreSessionStateId
  | TableQueryEventId
  | TableCoocQueryEventId
  | NotificationUserLinkId
  | NotificationId
  | SqlConfigId
  | CollapsibleCellId
  | TaoProjectId
  | TaoProjectAgentChatThreadLinkId
  | AgentChatThreadLinkId
  | DataConnectionTaoProjectLinkId
  | TaoPodId
  | TaoProjectSourceFileId
  | TaoProjectAOLogId
  | TaoAppId
  | TaoAppPodBindingId
  | TaoProjectGrantId
  | EditSessionBranchId
  | WorkloadPodBranchBindingId
  | SchemaRefreshEntityDiffId
  | SemanticProjectId
  | SemanticProjectVersionId
  | SemanticProjectVersionBuildId
  | SemanticProjectFileId
  | SemanticDatasetId
  | SemanticViewId
  | SemanticObjectCategoryLinkId
  | SemanticDatasetStatusLinkId
  | SemanticViewStatusLinkId
  | UserToGranteesId
  | SavedViewId
  | SavedViewHexScheduleLinkId
  | SavedViewScheduleNotificationLinkId
  | SemanticDatasetDataSourceTableLinkId
  | ContextSuggestionSourceId
  | ContextSuggestionIssueId
  | ContextSuggestionRunId
  | ContextSuggestionFileUpdateId
  | ContextSuggestionDataWarehouseUpdateId
  | ContextChangesetId
  | ContextVersionId
  | TableReferenceId
  | TableEntryId
  | DocusignEnvelopeId
  | ReceivedDocusignEventId
  | ReceivedStripeEventId
  | ReceivedWorkOSEventId
  | QueryCacheEntryId
  | HeartbeatId
  | HeroClusterId
  | HeroFindingId
  | TableDeletionQueueId
  | DataSourceShardDeletionQueueId
  | FileDeletionQueueId
  | OutputContentDeletionQueueId
  | UserToHubspotContactId
  | McpServerConfigurationId
  | UserMcpServerConnectionId
  | BillingCycleAiBillingUsageAggregationId
  | BillingCycleCreditGrantConsumptionAggregationId
  | CreditGrantConsumptionLogId
  | CreditGrantConsumptionId
  | CustomerEvalCaseRunId
  | CustomerEvalCaseRunAttemptId
  | CustomerEvalSuiteRunId
  | CustomerEvalSuiteId
  | CustomerEvalSuiteScheduleId
  | CustomerEvalSuiteVersionId
  | CustomerEvalCaseId
  | CustomerEvalCaseVersionId;

/**
 * Represents any possible entity or entity like id in our codebase
 */
export type SuperId =
  | DataBaseEntityId
  | IntrinsicGroupId
  | MapLayerId
  | KernelId
  | VcsAccessTokenId
  | PaymentMethodId
  | StaticDataConnectionId
  | StaticCellId
  | ParentDataConnectionId
  | FilterCellColumnId
  | ESDocId
  | KernelImage
  | PackageId
  | SearchHexesResultId
  | AgentThreadSearchResultId
  | StripeCustomerId
  | DisplayTableColumnId;
